// « Photo Vivante » on the admin order page (owner, 2026-11-03).
//
// Shows only when the order carries the option (website tick, options card,
// chat « oui », or a Shopify line — worker lib/livingPhoto.ts makes the rows).
// One line per album, in plain words:
//   Vidéo attendue → « Ajouter la vidéo » → the browser picks the sharpest
//   still, prepares what the camera looks for, uploads → Prête: the still to
//   print, the link, the QR, how many times it was scanned.
// The client can also add it from the album editor (LivingEditorPanel).
// The team never edits anything by hand. Worker: routes/living.ts.
//
// Two buttons (owner, 2026-11-04):
//   « Nouvelle page depuis la vidéo » — a client's phone video: its sharpest
//     frame prints as the last page (above);
//   « Choisir une photo de l'album » — a photo already printed in the album +
//     a video made FROM it (Google Flow…): the video is fitted onto the photo
//     (lib/livingOnPhoto.ts), nothing new prints.
// On an order WITHOUT the option the card stays folded: « Ajouter une Photo
// Vivante » opens one row per album (a demo, a gift) — nothing is billed.
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { livingApi, photosApi, sessionsApi, type LivingItem } from '@/lib/api';
import { addLivingVideo, type LivingStep } from '@/lib/livingUpload';
import { addLivingVideoOnPhoto, NotOnPhoto } from '@/lib/livingOnPhoto';
import { photoIdsInPageOrder } from '@/lib/livingAlbumPhotos';
import { Button } from '@/components/ui/Button';

type Step = LivingStep | null;

function QrImage({ link }: { link: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => { QRCode.toDataURL(link, { margin: 1, width: 360 }).then(setSrc).catch(() => setSrc(null)); }, [link]);
  return src ? <img src={src} alt="QR de la Photo Vivante" className="w-20 h-20 rounded-md border border-border-subtle bg-white" /> : null;
}

type PickPhoto = { id: string; page: number; thumb: string | null; full: string | null };

/** The album's photos in page order, to pick the one that comes alive. */
function PhotoPicker({ shopifyOrderId, albumIndex, onPick, onClose }: { shopifyOrderId: string; albumIndex: number; onPick: (p: PickPhoto) => void; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['living', 'album-photos', shopifyOrderId, albumIndex],
    staleTime: 60_000,
    queryFn: async (): Promise<PickPhoto[]> => {
      const { session } = await sessionsApi.get(shopifyOrderId, albumIndex);
      if (!session) return [];
      const { photos } = await photosApi.list(session.id);
      const byId = new Map(photos.map((p) => [p.id, p]));
      return photoIdsInPageOrder(session.pages).flatMap(({ id, page }) => {
        const p = byId.get(id);
        if (!p) return [];
        return [{ id, page, thumb: p.tray_signed_url ?? p.signed_url ?? null, full: p.preview_signed_url ?? p.original_signed_url ?? p.signed_url ?? null }];
      });
    },
  });
  return (
    <div className="mt-2 rounded-lg border border-border-subtle p-2">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-[12.5px] font-semibold text-text-primary m-0">Quelle photo prend vie ?</p>
        <button type="button" className="text-[12.5px] underline text-text-secondary" onClick={onClose}>Fermer</button>
      </div>
      {isLoading && <p className="text-[12.5px] text-text-secondary m-0 px-1">Chargement des photos…</p>}
      {error && <p className="text-[12.5px] text-red-600 m-0 px-1">Les photos n’ont pas pu être chargées.</p>}
      {data && data.length === 0 && <p className="text-[12.5px] text-text-secondary m-0 px-1">Cet album n’a pas encore de pages avec des photos.</p>}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-[320px] overflow-y-auto">
        {data?.map((p) => (
          <button key={`${p.page}-${p.id}`} type="button" disabled={!p.full} onClick={() => onPick(p)}
            className="relative aspect-[3/4] rounded-md overflow-hidden border border-border-subtle hover:ring-2 hover:ring-brand-500 bg-adm-surface-3">
            {p.thumb && <img src={p.thumb} alt="" loading="lazy" className="w-full h-full object-cover" />}
            <span className="absolute left-1 bottom-1 rounded bg-black/60 text-white text-[10.5px] px-1">p. {p.page}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AlbumRow({ item, shopifyOrderId, albums, onDone, title }: { item: LivingItem; shopifyOrderId: string; albums: number; onDone: () => void; title?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<PickPhoto | null>(null);
  const busy = step !== null;
  const ready = item.status === 'ready';
  const onAlbumPhoto = !!item.photo_id;

  async function run(file: File) {
    setError(null);
    try {
      await addLivingVideo(file, item.album_index, livingApi.team(shopifyOrderId), setStep);
      setStep(null);
      onDone();
    } catch (e) {
      setStep(null);
      setError((e as Error).message || 'Ça n’a pas marché. Réessayez.');
    }
  }

  async function remove() {
    const name = albums > 1 ? `de l’album ${item.album_index}` : 'de l’album';
    if (!window.confirm(`Supprimer la Photo Vivante ${name} ?\n\nLa vidéo est effacée. Le lien et le QR restent les mêmes : ils montreront « pas encore prête » jusqu’à une nouvelle vidéo.`)) return;
    setError(null);
    setStep({ label: 'Suppression…', pct: 50 });
    try {
      await livingApi.remove(shopifyOrderId, item.album_index);
      setStep(null);
      onDone();
    } catch (e) {
      setStep(null);
      setError((e as Error).message || 'La suppression n’a pas marché. Réessayez.');
    }
  }

  async function runOnPhoto(file: File, photo: PickPhoto, anyway = false) {
    setError(null);
    try {
      await addLivingVideoOnPhoto(file, item.album_index, { id: photo.id, url: photo.full! }, livingApi.team(shopifyOrderId), setStep, anyway);
      setStep(null); setPicked(null);
      onDone();
    } catch (e) {
      setStep(null);
      // A client's own edit rarely starts on the printed photo: allowed, it
      // then fills the photo from the first second (owner, 2026-11-04).
      if (e instanceof NotOnPhoto && window.confirm('La vidéo ne commence pas sur cette photo.\n\nL’utiliser quand même ? Elle remplira la photo dès le début (sans l’effet « la photo s’anime »).')) {
        return runOnPhoto(file, photo, true);
      }
      setError((e as Error).message || 'Ça n’a pas marché. Réessayez.');
    }
  }

  return (
    <div className="rounded-lg border border-border-subtle p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[13px] font-semibold text-text-primary m-0">{title ?? (albums > 1 ? `Album ${item.album_index}` : 'Album')}</p>
        {item.preparing
          ? <span className="text-[12px] font-semibold rounded-full px-2.5 py-0.5 bg-blue-50 text-blue-700">En préparation…</span>
          : ready
            ? <span className="text-[12px] font-semibold rounded-full px-2.5 py-0.5 bg-green-50 text-green-700">Prête ✓</span>
            : <span className="text-[12px] font-semibold rounded-full px-2.5 py-0.5 bg-amber-50 text-amber-800">Vidéo attendue</span>}
      </div>

      {ready && (
        <div className="flex gap-3 items-start">
          {item.still_url && (
            <a href={item.still_url} target="_blank" rel="noreferrer" title="Ouvrir la photo à imprimer">
              <img src={item.still_url} alt={onAlbumPhoto ? 'Photo de l’album qui prend vie' : 'Photo à imprimer en dernière page'} className="w-16 h-[95px] object-cover rounded-md border border-border-subtle" />
            </a>
          )}
          <QrImage link={item.link} />
          <div className="min-w-0 flex-1 text-[12.5px] text-text-secondary space-y-1">
            <p className="m-0 truncate text-text-primary">{item.link.replace('https://', '')}</p>
            <p className="m-0">{onAlbumPhoto ? 'Sur une photo de l’album : rien de plus à imprimer.' : 'Nouvelle dernière page.'} {item.scans > 0 ? `Scannée ${item.scans} fois` : 'Pas encore scannée'}</p>
            <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
              <button type="button" className="underline text-text-primary" onClick={() => { navigator.clipboard?.writeText(item.link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                {copied ? 'Lien copié ✓' : 'Copier le lien'}
              </button>
              {item.still_url && !onAlbumPhoto && <a className="underline text-text-primary" href={item.still_url} download={`photo-vivante-album-${item.album_index}.jpg`}>Photo à imprimer</a>}
              <a className="underline text-text-primary" href={item.link} target="_blank" rel="noreferrer">Tester</a>
              <button type="button" className="underline text-red-600" onClick={() => void remove()}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {item.preparing && !busy && (
        <p className="text-[12.5px] text-text-secondary m-0 mb-2">Vidéo reçue. Notre serveur la prépare (1 à 5 min selon la durée) : plus légère, fluide, prête à jouer. Vous pouvez fermer cette page.</p>
      )}
      {item.prep_error && !item.preparing && !busy && (
        <p className="text-[12.5px] text-red-600 m-0 mb-2">La dernière vidéo n’a pas pu être préparée : {item.prep_error}. Réessayez, ou envoyez-la en MP4.</p>
      )}
      {!ready && !item.preparing && !busy && (
        <p className="text-[12.5px] text-text-secondary m-0 mb-2">Le client peut l’ajouter depuis son éditeur. Sinon, demandez-lui sa vidéo sur WhatsApp (jusqu’à 2 minutes), puis ajoutez-la ici.</p>
      )}

      {busy && (
        <div className="mt-1">
          <p className="text-[12.5px] text-text-secondary m-0 mb-1.5">{step!.label}</p>
          <div className="h-1.5 rounded-full bg-adm-surface-3 overflow-hidden">
            <div className="h-full bg-brand-500 transition-[width] duration-300" style={{ width: `${Math.round(step!.pct)}%` }} />
          </div>
        </div>
      )}

      {error && <p className="text-[12.5px] text-red-600 m-0 mt-2">{error}</p>}

      {!busy && (
        <div className="mt-2 flex flex-wrap gap-2">
          <input ref={input} type="file" accept="video/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void run(f); }} />
          <input ref={photoInput} type="file" accept="video/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f && picked) void runOnPhoto(f, picked); }} />
          <Button variant={ready ? 'ghost' : 'primary'} size="sm" onClick={() => input.current?.click()}>
            Nouvelle page depuis la vidéo
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { setPicking((v) => !v); setPicked(null); }}>
            Choisir une photo de l’album
          </Button>
        </div>
      )}

      {!busy && picking && !picked && (
        <PhotoPicker shopifyOrderId={shopifyOrderId} albumIndex={item.album_index}
          onClose={() => setPicking(false)}
          onPick={(p) => { setPicked(p); setPicking(false); }} />
      )}

      {!busy && picked && (
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-border-subtle p-2">
          {picked.thumb && <img src={picked.thumb} alt="" className="w-12 h-16 object-cover rounded-md" />}
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] text-text-primary m-0">Photo de la page {picked.page}. La vidéo doit commencer sur cette photo.</p>
            <div className="flex gap-3 mt-1">
              <Button variant="primary" size="sm" onClick={() => photoInput.current?.click()}>Ajouter sa vidéo</Button>
              <button type="button" className="text-[12.5px] underline text-text-secondary" onClick={() => setPicked(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Rows to show: the real ones, plus an empty one per album not opened yet
 *  (only once the team unfolded the card on an order without the option). Pure. */
export function livingRowsToShow(items: readonly LivingItem[], albums: number, unfolded: boolean): LivingItem[] {
  if (!unfolded) return [...items];
  const have = new Set(items.map((i) => i.album_index));
  const extra: LivingItem[] = [];
  for (let i = 1; i <= Math.max(1, Math.min(10, albums)); i++) {
    if (!have.has(i)) extra.push({ album_index: i, status: 'waiting', scans: 0, last_scan_at: null, link: '', still_url: null, has_video: false, updated_at: '', photo_id: null });
  }
  return [...items, ...extra].sort((a, b) => a.album_index - b.album_index);
}

export function LivingPhotoCard({ shopifyOrderId, albums = 1 }: { shopifyOrderId: string; albums?: number }) {
  const qc = useQueryClient();
  const [unfolded, setUnfolded] = useState(false);
  const { data } = useQuery({
    queryKey: ['living', shopifyOrderId], queryFn: () => livingApi.forOrder(shopifyOrderId), staleTime: 30_000,
    // While the server prepares a video, look again every 10 s.
    refetchInterval: (q) => ((q.state.data?.items ?? []).some((i) => i.preparing) ? 10_000 : false),
  });
  if (!data) return null;
  const real = data.items ?? [];
  if (!real.length && !unfolded) {
    return (
      <div className="rounded-xl border border-border-subtle bg-bg-card px-4 py-3 flex items-center justify-between gap-3" data-living-card>
        <p className="text-[13px] text-text-secondary m-0">🎬 Photo Vivante : pas prise sur cette commande.</p>
        <button type="button" className="text-[12.5px] underline text-text-primary shrink-0" onClick={() => setUnfolded(true)}>Ajouter une Photo Vivante</button>
      </div>
    );
  }
  const items = livingRowsToShow(real, albums, unfolded || !data.sold);
  const done = items.filter((i) => i.status === 'ready').length;
  return (
    <div className="rounded-xl border border-border-subtle bg-bg-card p-4" data-living-card>
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-[13px] font-semibold text-text-primary m-0">🎬 Photo Vivante</p>
        <p className="text-[12px] text-text-secondary m-0">{done}/{items.length} prête{items.length > 1 ? 's' : ''}</p>
      </div>
      <p className="text-[12px] text-text-secondary m-0 mb-3">Le QR au dos de la couverture (ou le lien) ouvre la caméra : la photo joue la vidéo.{!data.sold && ' Option non facturée sur cette commande.'}</p>
      <div className="space-y-2">
        {items.map((it) => (
          <AlbumRow key={it.album_index} item={it} albums={items.length} shopifyOrderId={shopifyOrderId}
            onDone={() => qc.invalidateQueries({ queryKey: ['living', shopifyOrderId] })} />
        ))}
      </div>
    </div>
  );
}
